import React, { useState } from 'react';
import { QRCodeCanvas } from 'qrcode.react';
import { Download, X } from 'lucide-react';
import sectionTypesData from '../utils/sectionTypes.json';
import SectionList from './SectionList';

const STAFF_COLORS = ['#F3774D', '#60a5fa', '#fbbf24', '#a78bfa', '#f472b6', '#22d3ee', '#fb923c', '#a3e635'];

const MOCK_STAFF_POOL = [
  ['Arjun Mehta', 'Priya Sharma', 'Rohan Verma', 'Sneha Nair', 'Vikram Patel'],
  ['Ananya Iyer', 'Karthik Rao', 'Divya Joshi'],
  ['Rahul Gupta', 'Meera Reddy', 'Aditya Kumar', 'Pooja Singh', 'Nikhil Das', 'Kavya Menon'],
  ['Deepak Chauhan', 'Ishita Banerjee'],
];

const resolveSectionQueueStats = (section) => {
  const waiting = Number(
    section?.inQueueNow ?? section?.currentlyWaiting ?? section?.queueNow ?? 0
  );
  return Number.isFinite(waiting) ? Math.max(0, waiting) : 0;
};

const formatAvgWait = (section) => {
  const mins = Number(section?.avgWaitMins ?? section?.averageWaitMinutes ?? 0);
  if (!mins || !Number.isFinite(mins) || mins <= 0) return '—';
  const m = Math.floor(mins);
  const s = Math.round((mins - m) * 60);
  return `${m}:${String(s).padStart(2, '0')}`;
};

const StoreManageSections = ({
  showSections,
  parentSections,
  sectionStats,
  handleEditSection,
  handleToggleSectionStatus,
  handleDownloadQR,
  setDeleteSectionTarget,
  openCounterModal,
  openModal,
  openZoneModal,
  storeName,
  storeUuid
}) => {
  const [staffModal, setStaffModal] = useState(null);

  return (
    <SectionList visible={showSections}>
      <div className="page-h">
        <div>
          <div className="title">{`${parentSections.length} active section${parentSections.length !== 1 ? 's' : ''}`}</div>
          <div className="sub">Manage staff, services and live availability per section</div>
        </div>
        <div className="actions">
          <button type="button" className="btn-aux" onClick={openZoneModal}>+ Add Zone</button>
          <button type="button" className="btn-cta" onClick={openModal}>+ New section</button>
        </div>
      </div>

      <div className="sect-grid">
        {parentSections.length === 0 && (
          <div className="empty">No sections added yet.</div>
        )}
        {parentSections.map((section, idx) => {
          const meta = sectionTypesData[section.sectionType] || { label: 'Section', unitLabel: 'Counters', manageLabel: 'Manage', className: 'default' };
          const stats = sectionStats[section.sectionUuid] || {};
          const total = Number.isFinite(stats.total) ? stats.total : (section.sectionWindows || 0);
          const active = Number.isFinite(stats.active) ? stats.active : total;
          const isSectionActive = Number(section?.rowStatus ?? 1) === 1;
          const queueCount = resolveSectionQueueStats(section);
          const avgWait = formatAvgWait(section);
          const serviceCount = Number(section?.serviceCount ?? section?.services ?? total);

          const realStaff = Array.isArray(section?.staffNames) ? section.staffNames : [];
          const staffNames = realStaff.length > 0 ? realStaff : (MOCK_STAFF_POOL[idx % MOCK_STAFF_POOL.length] || []);
          const staffAvatars = staffNames.slice(0, 4).map((name, i) => ({
              a: name.trim().charAt(0).toUpperCase(),
              c: STAFF_COLORS[i % STAFF_COLORS.length]
            }));

          return (
            <div className="card sect-card" key={section.sectionUuid || `section-${idx}`}>
              <div className="h">
                <div>
                  <div className="nm">{section.sectionName || 'Unnamed section'}</div>
                  <div className="ms">
                    {section.sectionDescription || meta.label}
                    {section.floorNumber != null && ` · Floor ${section.floorNumber}`}
                    {section.sectionWing && ` · ${section.sectionWing}`}
                  </div>
                </div>
                <div className={`status ${isSectionActive ? 'live' : 'paused'}`}>
                  {isSectionActive ? 'Live' : 'Paused'}
                </div>
              </div>

              <div className="stats">
                <div className="x">
                  <div className="v" style={{ color: queueCount > 0 ? '#f1f5f9' : '#5a6578' }}>{queueCount}</div>
                  <div className="l">In queue</div>
                </div>
                <div className="x">
                  <div className="v" style={{ color: avgWait !== '—' ? 'var(--color-accent)' : '#5a6578' }}>{avgWait}</div>
                  <div className="l">Avg wait</div>
                </div>
                <div className="x">
                  <div className="v">{serviceCount}</div>
                  <div className="l">Services</div>
                </div>
              </div>

              <div className="sect-card-detail">
                <span>Grace: {section.gracePeriodMins ?? '—'} min</span>
              </div>

              <div className="counter-badge" onClick={() => openCounterModal(section)}>
                <span className="counter-badge-total">{total}</span>
                <span className="counter-badge-label">{meta.unitLabel.toLowerCase()}</span>
                <span className="counter-badge-sep" />
                <span className={`counter-badge-dot ${active > 0 ? 'on' : ''}`} />
                <span className="counter-badge-active">{active} active</span>
              </div>

              <div
                className={`staff-row ${staffNames.length > 0 ? 'clickable' : ''}`}
                onClick={staffNames.length > 0 ? () => setStaffModal({ sectionName: section.sectionName, staffNames }) : undefined}
              >
                {staffNames.length > 0 ? (
                  <>
                    {staffAvatars.map((p, j) => (
                      <div className="av" key={j} style={{ background: p.c }}>{p.a}</div>
                    ))}
                    <div className="more">
                      {staffNames.length} staff{staffNames.length > 4 ? ` (+${staffNames.length - 4} more)` : ''}
                    </div>
                  </>
                ) : (
                  <div className="no-staff">No staff assigned</div>
                )}
              </div>

              <div className="ftr">
                <div className="b" onClick={() => handleEditSection(section)}>Edit</div>
                <div className="b btn-manage" onClick={() => openCounterModal(section)}>{meta.manageLabel}</div>
                <div className="b" onClick={() => handleDownloadQR(section)} title="Download QR">
                  <Download size={11} style={{ marginRight: 3, verticalAlign: '-1px' }} />QR
                </div>
                <div
                  className="b"
                  style={{ marginLeft: 'auto', color: isSectionActive ? '#fbbf24' : 'var(--color-accent)', borderColor: 'transparent' }}
                  onClick={() => handleToggleSectionStatus(section)}
                >
                  {isSectionActive ? 'Pause' : 'Resume'}
                </div>
              </div>

              {/* Hidden QR poster for download */}
              <div
                id={`qr-poster-${section.sectionUuid}`}
                style={{
                  display: 'none', position: 'absolute', top: 0, left: 0, zIndex: -1,
                  width: '400px', padding: '40px',
                  background: 'linear-gradient(145deg, #000000 0%, #111111 100%)',
                  borderRadius: '32px', border: '1px solid rgba(148, 163, 184, 0.1)',
                  flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                  gap: '32px', boxShadow: '0 24px 48px rgba(0,0,0,0.5)',
                  color: '#f8fafc', fontFamily: '"Inter", -apple-system, sans-serif', textAlign: 'center'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <h1 style={{ margin: 0, fontSize: '28px', fontWeight: 800, letterSpacing: '-0.02em', background: 'linear-gradient(90deg, #31e082, #2ad988)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                    {storeName || 'Store'}
                  </h1>
                  <p style={{ margin: 0, fontSize: '18px', fontWeight: 500, color: 'rgba(248, 250, 252, 0.8)' }}>
                    {section.sectionName || 'Section'}
                  </p>
                </div>
                <div style={{ background: '#ffffff', padding: '20px', borderRadius: '24px', boxShadow: '0 16px 32px rgba(243, 119, 77, 0.1)' }}>
                  <QRCodeCanvas
                    id={`qr-canvas-${section.sectionUuid}`}
                    value={`https://app.zopeo.in/queue/${storeUuid}/${section.sectionUuid}`}
                    size={220} level="H" includeMargin={false} fgColor="#000000"
                  />
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  <p style={{ margin: 0, fontSize: '15px', fontWeight: 600 }}>Scan with your camera</p>
                  <p style={{ margin: 0, fontSize: '13px', color: 'rgba(248, 250, 252, 0.5)' }}>to securely join the queue</p>
                </div>
                <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '8px', borderTop: '1px solid rgba(148, 163, 184, 0.1)', paddingTop: '24px', width: '100%', justifyContent: 'center' }}>
                  <span style={{ fontSize: '12px', color: 'rgba(248, 250, 252, 0.5)', fontWeight: 500 }}>Powered by</span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    <div style={{ width: '16px', height: '16px', background: '#31e082', borderRadius: '4px' }} />
                    <span style={{ fontSize: '14px', fontWeight: 700, letterSpacing: '-0.02em' }}>Z<span style={{ color: '#31e082' }}>o</span>pe<span style={{ color: '#31e082' }}>o</span></span>
                  </div>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Staff modal */}
      {staffModal && (
        <div className="zone-modal-overlay" onClick={() => setStaffModal(null)}>
          <div className="staff-modal" onClick={(e) => e.stopPropagation()}>
            <div className="staff-modal-header">
              <div className="staff-modal-title">{staffModal.sectionName || 'Section'} — Staff</div>
              <button className="staff-modal-close" onClick={() => setStaffModal(null)}><X size={16} /></button>
            </div>
            <div className="staff-modal-body">
              {staffModal.staffNames.length === 0 ? (
                <div className="staff-modal-empty">No staff assigned to this section yet.</div>
              ) : (
                staffModal.staffNames.map((name, i) => (
                  <div className="staff-modal-row" key={i}>
                    <div className="staff-modal-av" style={{ background: STAFF_COLORS[i % STAFF_COLORS.length] }}>
                      {name.trim().charAt(0).toUpperCase()}
                    </div>
                    <div className="staff-modal-name">{name}</div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </SectionList>
  );
};

export default StoreManageSections;
